import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { CompanyRegistrationData } from '../../types/company';
import { BUSINESS_TYPES, EMPLOYEE_COUNT_RANGES } from '../../constants/company';

interface ConfirmationStepProps {
  register: UseFormRegister<CompanyRegistrationData>;
  errors: FieldErrors<CompanyRegistrationData>;
  watchedValues: Partial<CompanyRegistrationData>;
  onFieldFocus: (fieldName: keyof CompanyRegistrationData) => void;
  onEdit: (step: number) => void;
}

export const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  register,
  errors,
  watchedValues,
  onFieldFocus,
  onEdit,
}) => {
  // 業種の表示名を取得
  const getBusinessTypeLabel = (value: string) => {
    const item = BUSINESS_TYPES.find((type) => type.value === value);
    return item?.label || value;
  };

  // 従業員数規模の表示名を取得
  const getEmployeeCountLabel = (value: string) => {
    const item = EMPLOYEE_COUNT_RANGES.find((range) => range.value === value);
    return item?.label || value;
  };

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
        登録内容の確認
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        入力された内容をご確認ください。修正が必要な場合は各セクションの「編集」ボタンをクリックしてください。
      </Typography>

      {/* 企業情報確認 */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            企業情報
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={() => onEdit(0)}
            sx={{ textDecoration: 'none' }}
          >
            編集
          </Link>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              企業名
            </Typography>
            <Typography variant="body1" data-testid="confirm-company-name">
              {watchedValues.companyName || '未入力'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              代表者氏名
            </Typography>
            <Typography variant="body1">
              {watchedValues.representativeName || '未入力'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              所在地
            </Typography>
            <Typography variant="body1">
              {watchedValues.prefecture && watchedValues.city
                ? `${watchedValues.prefecture} ${watchedValues.city}`
                : '未入力'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              業種
            </Typography>
            <Typography variant="body1">
              {watchedValues.businessType
                ? getBusinessTypeLabel(watchedValues.businessType)
                : '未選択'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              従業員数規模
            </Typography>
            <Typography variant="body1">
              {watchedValues.employeeCountRange
                ? getEmployeeCountLabel(watchedValues.employeeCountRange)
                : '未選択'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* 代表者情報確認 */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            代表者情報
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={() => onEdit(1)}
            sx={{ textDecoration: 'none' }}
          >
            編集
          </Link>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              代表者メールアドレス
            </Typography>
            <Typography variant="body1">
              {watchedValues.contactEmail || '未入力'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              パスワード
            </Typography>
            <Typography variant="body1">
              {watchedValues.password ? '●●●●●●●●' : '未設定'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* 利用規約・プライバシーポリシー同意 */}
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          利用規約・プライバシーポリシー同意
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              {...register('agreedToTerms')}
              color="primary"
              onFocus={() => onFieldFocus('agreedToTerms')}
              inputProps={{ 'data-testid': 'terms-agreement' } as any}
            />
          }
          label={
            <Typography variant="body2">
              <Link href="/terms/company" target="_blank" rel="noopener noreferrer">
                企業向け利用規約
              </Link>
              に同意する
            </Typography>
          }
        />
        {errors.agreedToTerms && (
          <Typography
            variant="caption"
            color="error"
            display="block"
            sx={{ mt: 1 }}
          >
            {errors.agreedToTerms.message}
          </Typography>
        )}

        <FormControlLabel
          control={
            <Checkbox
              {...register('agreedToPrivacy')}
              color="primary"
              onFocus={() => onFieldFocus('agreedToPrivacy')}
              inputProps={{ 'data-testid': 'privacy-agreement' } as any}
            />
          }
          label={
            <Typography variant="body2">
              <Link href="/privacy/company" target="_blank" rel="noopener noreferrer">
                企業向けプライバシーポリシー
              </Link>
              に同意する
            </Typography>
          }
        />
        {errors.agreedToPrivacy && (
          <Typography
            variant="caption"
            color="error"
            display="block"
            sx={{ mt: 1 }}
          >
            {errors.agreedToPrivacy.message}
          </Typography>
        )}
      </Box>

      {/* 注意事項 */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
        <Typography variant="body2" color="info.contrastText">
          <strong>審査について:</strong>
        </Typography>
        <Typography variant="body2" color="info.contrastText" sx={{ mt: 1 }}>
          • 申請後、管理者による審査が行われます（通常3営業日）
        </Typography>
        <Typography variant="body2" color="info.contrastText">
          • 審査結果は登録メールアドレスにお知らせいたします
        </Typography>
        <Typography variant="body2" color="info.contrastText">
          • 承認後、企業アカウントがご利用いただけるようになります
        </Typography>
      </Box>
    </Box>
  );
};
